import { RuntimeLoader, useRive } from '@rive-app/react-canvas-lite';
import * as Sentry from '@sentry/react';
import { Asset } from 'expo-asset';
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';

import { type RiveAnimationSource } from '@/constants/riveAnimations';
import { RIVE_WASM_URL } from '@/constants/riveRuntime';

/**
 * Points the runtime at our self-hosted WASM before anything can load it. This only sets
 * the address, so it is free, and the download itself waits for the first animation
 * to mount. It has to run before the first `useRive`, which is why it sits at module scope
 * rather than in the component.
 */
RuntimeLoader.setWasmUrl(RIVE_WASM_URL);

type RiveAdapterProps = {
  source: RiveAnimationSource;
  autoplay?: boolean;
};

type RiveRef = {
  play: () => void;
  pause: () => void;
  reset: () => void;
  stop: () => void;
};

export const RiveAdapter = forwardRef<RiveRef, RiveAdapterProps>(
  ({ source, autoplay = true }, ref) => {
    /** The hashed URL Metro gave the bundled file, served from our own origin. */
    const src = useMemo(() => Asset.fromModule(source).uri, [source]);

    /**
     * The Rive runtime logs a bare "Problem loading file" line and rejects an
     * internal promise when a `.riv` fails to fetch or decode, naming neither
     * the file nor the reason. That line is filtered out of Sentry precisely
     * because it is unreadable, so this reports the same failure with the URL
     * attached. Otherwise a broken asset shows up only as a blank space on the
     * screen that was waiting for it.
     */
    const onLoadError = useCallback(() => {
      Sentry.captureException(new Error('Rive animation failed to load'), {
        tags: {
          component: 'RiveAdapter',
          source: 'rive-load-error',
        },
        extra: {
          url: src,
        },
      });
    }, [src]);

    const { rive, RiveComponent } = useRive({
      src,
      autoplay,
      onLoadError,
    });

    useImperativeHandle(ref, () => {
      return {
        play: () => {
          rive?.play();
        },
        pause: () => {
          rive?.pause();
        },
        stop: () => {
          rive?.stop();

          // we need to reset the animation to be able to call 'play'
          rive?.reset();
        },
        reset: () => {
          // In order to play the animation we need to reset it first
          rive?.reset();

          rive?.play();
        },
      };
    }, [rive]);

    /** Every animation is decorative, so a screen reader skips it. */
    return <RiveComponent aria-hidden />;
  }
);
